import { NgTemplateOutlet } from '@angular/common';
import { CdkConnectedOverlay, CdkOverlayOrigin, type ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Button2Component } from '../button/button2.component';
import { IconComponent } from '../icon/icon.component';
import {
  type SelectAction,
  type SelectActionEvent,
  type SelectItem,
  type SelectRowContext,
  type SelectTriggerContext,
} from './multi-select-button.types';

let nextPanelId = 0;

@Component({
  selector: 'app-multi-select-button',
  imports: [Button2Component, CdkConnectedOverlay, CdkOverlayOrigin, IconComponent, NgTemplateOutlet],
  templateUrl: './multi-select-button.component.html',
  styleUrl: './multi-select-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectButtonComponent {
  public readonly items = input<readonly SelectItem[]>([]);
  public readonly pinnedItems = input<readonly SelectItem[]>([]);
  public readonly selectedIds = input<readonly string[]>([]);
  public readonly selectionReadonly = input(false);
  public readonly selectionPending = input(false);
  public readonly disabled = input(false);
  public readonly searchable = input(true);
  public readonly searchPlaceholder = input('Search');
  public readonly searchMode = input<'local' | 'external'>('local');
  public readonly label = input('Select');
  public readonly topText = input<string | null>(null);
  public readonly bottomText = input<string | null>(null);
  public readonly action = input<SelectAction | null>(null);
  public readonly loading = input(false);
  public readonly errorText = input<string | null>(null);
  public readonly emptyText = input('No items available');
  public readonly noResultsText = input('No results');
  public readonly hasMore = input(false);
  public readonly loadingMore = input(false);
  public readonly rowTemplate = input<TemplateRef<SelectRowContext> | null>(null);
  public readonly triggerTemplate = input<TemplateRef<SelectTriggerContext> | null>(null);

  public readonly selectedIdsChange = output<readonly string[]>();
  public readonly actionClick = output<SelectActionEvent>();
  public readonly searchChange = output<string>();
  public readonly loadMore = output<void>();
  public readonly retry = output<void>();

  protected readonly panelId = `app-multi-select-panel-${++nextPanelId}`;
  protected readonly opened = signal(false);
  protected readonly searchQuery = signal('');
  protected readonly trigger = viewChild.required(Button2Component);
  protected readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  protected readonly searchField = viewChild<ElementRef<HTMLInputElement>>('searchField');
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected readonly allItems = computed<readonly SelectItem[]>(() => {
    const seen = new Set<string>();
    return [...this.pinnedItems(), ...this.items()].filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  });
  protected readonly visibleItems = computed<readonly SelectItem[]>(() => {
    if (this.searchMode() === 'external') return this.itemsWithoutPinned();
    const query = this.searchQuery().trim().toLocaleLowerCase();
    return this.itemsWithoutPinned().filter((item) =>
      `${item.label} ${item.description ?? ''}`.toLocaleLowerCase().includes(query),
    );
  });
  protected readonly visiblePinnedItems = computed<readonly SelectItem[]>(() =>
    this.allItems().slice(0, this.uniquePinnedCount()),
  );
  protected readonly selectedItems = computed<readonly SelectItem[]>(() => {
    const selected = new Set(this.selectedIds());
    return this.allItems().filter((item) => selected.has(item.id));
  });
  protected readonly selectedIdSet = computed<ReadonlySet<string>>(() => new Set(this.selectedIds()));
  protected readonly triggerContext = computed<SelectTriggerContext>(() => ({
    $implicit: this.selectedItems(),
    selectedIds: this.selectedIds(),
    opened: this.opened(),
  }));

  public constructor() {
    effect(() => {
      if (this.disabled() && this.opened()) this.close();
    });
  }

  public close(): void {
    if (!this.opened()) return;
    this.opened.set(false);
    this.searchQuery.set('');
    this.searchChange.emit('');
    queueMicrotask(() => this.trigger().focus());
  }

  protected toggleOpen(): void {
    if (this.disabled()) return;
    if (this.opened()) this.close();
    else this.opened.set(true);
  }

  protected focusFirstControl(): void {
    queueMicrotask(() => {
      const panel = this.panel()?.nativeElement;
      (panel?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])') ?? panel)?.focus();
    });
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const panel = this.panel()?.nativeElement;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])'),
      );
      if (!focusable.length) return;
      event.preventDefault();
      const currentIndex = focusable.indexOf(
        document.activeElement instanceof HTMLElement ? document.activeElement : panel,
      );
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      focusable[(currentIndex + direction + focusable.length) % focusable.length].focus();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = this.panel()?.nativeElement;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])'));
    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const currentIndex = focusable.indexOf(
      document.activeElement instanceof HTMLElement ? document.activeElement : panel,
    );
    if (event.shiftKey && currentIndex <= 0) {
      event.preventDefault();
      focusable[focusable.length - 1].focus();
    } else if (!event.shiftKey && currentIndex === focusable.length - 1) {
      event.preventDefault();
      focusable[0].focus();
    }
  }

  protected onSearchInput(event: Event): void {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.searchQuery.set(event.target.value);
    this.searchChange.emit(event.target.value);
  }

  protected onItemChange(item: SelectItem, event: Event): void {
    if (
      !(event.target instanceof HTMLInputElement) ||
      item.disabled ||
      this.selectionReadonly() ||
      this.selectionPending()
    )
      return;
    const requestedChecked = event.target.checked;
    event.target.checked = this.selectedIdSet().has(item.id);
    const next = new Set(this.selectedIds());
    if (requestedChecked) next.add(item.id);
    else next.delete(item.id);
    this.selectedIdsChange.emit([...next]);
  }

  protected onAction(): void {
    const action = this.action();
    if (!action || action.disabled || action.pending) return;
    this.actionClick.emit({ actionId: action.id, selectedIds: [...this.selectedIds()] });
  }

  private uniquePinnedCount(): number {
    return new Set(this.pinnedItems().map((item) => item.id)).size;
  }

  private itemsWithoutPinned(): readonly SelectItem[] {
    return this.allItems().slice(this.uniquePinnedCount());
  }
}
